/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, DoCheck, Inject, Input, NgZone, OnDestroy, signal} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import * as _ from 'lodash';

import template from './retail-product-list-page.component.html';
import {AdminListColumn, AdminResourceListPageBase} from '../common/controller/admin-resource-list-page-base';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {
  RetailProductCreativesModalComponent,
  RetailProductCreativesModalResult
} from './retail-product-creatives-modal.component';
import {RetailProductConversionTrackingModalComponent} from './retail-product-conversion-tracking-modal.component';
import {LayoutResource} from '../../components/api/resources/resource/layout.resource';
import {ProductFeedFacetsResource} from '../../components/api/resources/resource/product-feed.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {StateService} from '@uirouter/core';

@Component({
  standalone: false,
  selector: 'adn-retail-product-list-page',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RetailProductListPageComponent extends AdminResourceListPageBase implements DoCheck, OnDestroy {
  @Input() selectEnabled: boolean;
  @Input() createHook: (item: any) => void | Promise<any>;
  @Input() readOnly: boolean;
  @Input() showConversionTracking: boolean;

  readonly itemStateName = 'app.adv.retail-products.retail-product';

  /** Buffered so list-helper fill cannot NG0100 row bindings (e.g. price null→25). */
  readonly displayItems = signal<any[]>([]);
  readonly bulkSelectSize = signal(0);
  readonly creatingCreatives = signal(false);

  refLayout: any;

  private afterFillHooked = false;
  private selectionWrapped = false;
  private hookedList: any;
  private lastBulkSelectSize: number | undefined;
  private destroyed = false;

  constructor(
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LayoutResource) private readonly Layout: any,
    private readonly $state: StateService,
    private readonly toastr: ToastrService,
    @Inject(ProductFeedFacetsResource) private readonly ProductFeedFacets: any,
    private readonly angularModalService: AngularModalService,
    private readonly ngZone: NgZone
  ) {
    super(LocalNetworkProfile);
  }

  get visibleColumns(): AdminListColumn[] {
    return this.normalizeColumns(this.list?.sel?.selFields || []);
  }

  ngDoCheck(): void {
    const bulkSize = this.list?.bulkSelectSize || 0;
    if (bulkSize !== this.lastBulkSelectSize) {
      this.lastBulkSelectSize = bulkSize;
      // Macrotask: signal writes in a microtask re-enter synchronize (NG0103).
      setTimeout(() => {
        if (!this.destroyed) {
          this.bulkSelectSize.set(bulkSize);
        }
      }, 0);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  filtersResolved(): boolean {
    const query = this.list?.retailProductQuery;
    return !query || !!query.$resolved;
  }

  selected(item: any): void {
    if (_.isFunction(this.createHook)) {
      this.createHook(item);
    }
  }

  selectToggle(item: any): void {
    this.list?.selectToggle?.(item);
  }

  openConversionTracking(product: any): void {
    this.angularModalService.show(RetailProductConversionTrackingModalComponent, {
      initialState: {product},
      class: 'modal-md'
    });
  }

  createCreatives(): void {
    if (this.creatingCreatives()) {
      return;
    }

    if (_.isFunction(this.createHook)) {
      this.creatingCreatives.set(true);
      Promise.resolve(this.createHook(this.refLayout)).catch(() => undefined).finally(() => {
        if (!this.destroyed) {
          this.creatingCreatives.set(false);
        }
      });
      return;
    }

    const bulkSelection = _.map(_.cloneDeep(this.list.bulkSelection), (el: any) => el);
    const modalRef = this.angularModalService.show(RetailProductCreativesModalComponent, {
      initialState: {bulkSelection},
      class: 'modal-md'
    });
    (modalRef.content as RetailProductCreativesModalComponent).onCreated.subscribe(
      (result: RetailProductCreativesModalResult) => {
        this.ngZone.run(() => {
          if (result.destination && result.model?.id) {
            this.$state.go('app.adv.line-items.line-item', {id: result.model.id}, {inherit: false});
          } else {
            this.toastr.success('Creatives were successfully created.', 'Creatives Created');
          }
        });
      }
    );
  }

  protected afterListInit(): void {
    if (!this.list) {
      return;
    }
    this.list.canExpand = false;

    if (!this.list.merchantGuide) {
      this.list.merchantGuide = LocalNetworkInfo.obtain().getMerchantGuide();
    }

    this.hookDisplayItems();
    this.wrapSelectionHandlers();
    this.loadFacets();

    if (_.isFunction(this.createHook)) {
      this.getLayout(this);
    }
  }

  private hookDisplayItems(): void {
    if (this.hookedList !== this.list) {
      this.hookedList = this.list;
      this.afterFillHooked = false;
      this.selectionWrapped = false;
    }
    if (this.list.items?.length) {
      this.displayItems.set(this.list.items.slice());
    }
    if (this.afterFillHooked || !_.isFunction(this.list.addAfterFill)) {
      return;
    }
    this.afterFillHooked = true;
    this.list.addAfterFill((items: any[]) => {
      const snapshot = (items || []).slice();
      setTimeout(() => {
        if (!this.destroyed) {
          this.displayItems.set(snapshot);
        }
      }, 0);
    });
  }

  /** Header select-all mutates list maps in place — refresh OnPush signals. */
  private wrapSelectionHandlers(): void {
    if (this.selectionWrapped || !this.list) {
      return;
    }
    this.selectionWrapped = true;
    _.forEach(['selectAll', 'deselectAll', 'selectToggle'], (name: string) => {
      const original = this.list[name];
      if (!_.isFunction(original)) {
        return;
      }
      this.list[name] = (...args: any[]) => {
        const result = original.apply(this.list, args);
        this.afterSelectionChange();
        return result;
      };
    });
  }

  private afterSelectionChange(): void {
    this.bulkSelectSize.set(this.list?.bulkSelectSize || 0);
    this.displayItems.update((items) => items.slice());
  }

  private loadFacets(): void {
    const facetsLoaded = () => {
      const facets = LocalNetworkInfo.obtain().getInfo('retailProductFacets');
      this.list.merchants = _.filter(facets.merchants, (f: any) => f && f.id);
      this.list.colours = _.filter(facets.colours, (f: any) => f.facet);
      this.list.brands = _.filter(facets.brands, (f: any) => f.facet);
      // Facet filters render from list — bump CD after async facet hydrate.
      this.displayItems.update((items) => items.slice());
    };

    const facets = LocalNetworkInfo.obtain().getInfo('retailProductFacets');
    if (!facets || facets.merchants.length < 1) {
      this.ProductFeedFacets.getFacets().then((pf: any) => {
        LocalNetworkInfo.obtain().setRetailProductFacets(pf);
        facetsLoaded();
      });
    } else {
      facetsLoaded();
    }
  }

  private getLayout(obj: any): void {
    this.Layout.query().$promise.then((page: any) => {
      _.forEach(page.results, (layout: any) => {
        if (layout.detectedCategory === 'RETAIL') {
          obj.refLayout = layout;
        }
      });
    });
  }
}
